import React, { useCallback, useEffect, useState } from 'react';
import {
  Shield, ShieldOff, RefreshCw, Plus, Trash2, Save, User2,
  AlertTriangle, Ban, Clock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Melbourne',
    });
  } catch { return iso; }
};

const IpBlocklistTab = ({ token }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null); // ip or 'new'

  // New-block form
  const [newIp, setNewIp] = useState('');
  const [newNick, setNewNick] = useState('');
  const [newReason, setNewReason] = useState('');
  const [newHours, setNewHours] = useState(''); // blank = permanent

  const auth = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/blocked-ips`, { headers: auth });
      if (!res.ok) throw new Error('Failed to load blocklist');
      const data = await res.json();
      setRows(data.blocked || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const block = async (e) => {
    e.preventDefault();
    if (!newIp.trim()) return;
    setBusy('new');
    try {
      const body = {
        ip: newIp.trim(),
        reason: newReason.trim() || 'Manually blocked by admin',
      };
      if (newNick.trim()) body.nickname = newNick.trim();
      if (newHours && Number(newHours) > 0) body.duration_hours = Number(newHours);
      const res = await fetch(`${API}/api/admin/blocked-ips`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Block failed');
      }
      setNewIp(''); setNewNick(''); setNewReason(''); setNewHours('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const patch = async (ip, updates) => {
    setBusy(ip);
    try {
      const res = await fetch(`${API}/api/admin/blocked-ips/${encodeURIComponent(ip)}`, {
        method: 'PATCH',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Update failed');
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const unblock = async (ip) => {
    if (!window.confirm(`Unblock ${ip}? They will regain full site access.`)) return;
    setBusy(ip);
    try {
      const res = await fetch(`${API}/api/admin/blocked-ips/${encodeURIComponent(ip)}`, {
        method: 'DELETE',
        headers: auth,
      });
      if (!res.ok) throw new Error('Unblock failed');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const updateField = (ip, field, value) => {
    setRows((prev) => prev.map((r) => (r.ip === ip ? { ...r, [field]: value } : r)));
  };

  return (
    <div className="space-y-5" data-testid="ip-blocklist-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="h-4 w-4 text-red-500" />
            <h3 className="text-sm font-black tracking-widest text-red-400 uppercase">
              IP Blocklist
            </h3>
          </div>
          <p className="text-sm text-gray-400">
            IPs listed here are locked out of the <strong className="text-white">entire</strong> site
            - every page loads the ban notice instead of the app. Each entry
            tracks how often they&apos;ve tried to come back and how many post
            attempts they&apos;ve made since being blocked.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="ip-blocklist-refresh"
          className="flex items-center gap-1.5 bg-zinc-900 hover:bg-zinc-800 border border-red-900/30 text-gray-300 hover:text-white font-bold px-3 py-1.5 rounded text-xs"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* Add new block */}
      <form
        onSubmit={block}
        className="bg-zinc-900/80 backdrop-blur-xl border border-red-500/25 rounded-lg p-4 space-y-3"
        data-testid="ip-block-create-form"
      >
        <p className="text-xs font-black tracking-widest text-red-400 uppercase flex items-center gap-1.5">
          <Ban className="h-3.5 w-3.5" /> Block a new IP
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            required
            value={newIp}
            onChange={(e) => setNewIp(e.target.value)}
            placeholder="IP address - e.g. 203.0.113.44"
            data-testid="ip-block-new-ip"
            maxLength={64}
            className="bg-black/50 border border-red-500/30 text-white px-3 py-2 rounded text-sm font-mono focus:outline-none focus:border-red-500"
          />
          <input
            value={newNick}
            onChange={(e) => setNewNick(e.target.value)}
            placeholder="Nickname - e.g. Frankston threats guy"
            data-testid="ip-block-new-nickname"
            maxLength={80}
            className="bg-black/50 border border-red-500/30 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            placeholder="Reason (public + audit log)"
            data-testid="ip-block-new-reason"
            maxLength={200}
            className="sm:col-span-2 bg-black/50 border border-red-500/30 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
          />
          <input
            value={newHours}
            onChange={(e) => setNewHours(e.target.value)}
            placeholder="Hours (blank = forever)"
            data-testid="ip-block-new-hours"
            type="number"
            min="0"
            className="bg-black/50 border border-red-500/30 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
          />
        </div>
        <button
          type="submit"
          disabled={busy === 'new' || !newIp.trim()}
          data-testid="ip-block-add-btn"
          className="w-full sm:w-auto bg-red-600 hover:bg-red-500 text-white disabled:opacity-40 font-black text-xs uppercase tracking-widest px-5 py-2 rounded flex items-center justify-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Block IP
        </button>
      </form>

      {error && (
        <div
          className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-3 rounded text-sm"
          data-testid="ip-block-error"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400" data-testid="ip-block-loading">Loading blocklist…</div>
      ) : rows.length === 0 ? (
        <div
          className="bg-zinc-900/60 border border-red-900/30 rounded-lg p-8 text-center text-gray-400"
          data-testid="ip-block-empty"
        >
          No IPs currently blocked. Add one above.
        </div>
      ) : (
        <div className="space-y-3" data-testid="ip-block-list">
          {rows.map((r) => {
            const permanent = !r.expires_at;
            const isBusy = busy === r.ip;
            return (
              <div
                key={r.ip}
                data-testid={`ip-block-item-${r.ip}`}
                className="bg-zinc-900/80 backdrop-blur-xl border border-red-500/25 rounded-lg p-3 space-y-3"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-sm font-black text-red-300 break-all" data-testid={`ip-block-ip-${r.ip}`}>
                        {r.ip}
                      </span>
                      <span className={`text-[9px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded ${
                        r.source === 'manual' ? 'bg-red-900/40 text-red-200' : 'bg-yellow-900/40 text-yellow-200'
                      }`}>
                        {r.source}
                      </span>
                      <span className={`text-[9px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded ${
                        permanent ? 'bg-black border border-red-500/40 text-red-300' : 'bg-zinc-800 text-gray-300'
                      }`}>
                        {permanent ? 'Permanent' : `Expires ${fmtDate(r.expires_at)}`}
                      </span>
                    </div>
                    <p className="text-[10px] uppercase tracking-widest text-gray-500">
                      Blocked {fmtDate(r.blocked_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => unblock(r.ip)}
                    disabled={isBusy}
                    data-testid={`ip-block-unblock-${r.ip}`}
                    className="flex items-center gap-1.5 bg-emerald-700/40 hover:bg-emerald-600/60 text-emerald-100 disabled:opacity-40 font-black text-[11px] uppercase tracking-widest px-3 py-1.5 rounded"
                  >
                    <ShieldOff className="h-3.5 w-3.5" />
                    Unblock
                  </button>
                </div>

                {/* Editable nickname + reason */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1 flex items-center gap-1">
                      <User2 className="h-3 w-3" /> Nickname
                    </label>
                    <input
                      value={r.nickname || ''}
                      onChange={(e) => updateField(r.ip, 'nickname', e.target.value)}
                      placeholder="Add a nickname…"
                      data-testid={`ip-block-nickname-${r.ip}`}
                      maxLength={80}
                      className="w-full bg-black/50 border border-red-500/25 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Reason
                    </label>
                    <input
                      value={r.reason || ''}
                      onChange={(e) => updateField(r.ip, 'reason', e.target.value)}
                      placeholder="Why they were banned"
                      data-testid={`ip-block-reason-${r.ip}`}
                      maxLength={200}
                      className="w-full bg-black/50 border border-red-500/25 text-white px-3 py-2 rounded text-sm focus:outline-none focus:border-red-500"
                    />
                  </div>
                </div>

                {/* Counters */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-black/40 rounded px-3 py-2 border border-red-500/15" data-testid={`ip-block-access-${r.ip}`}>
                    <div className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">Site attempts</div>
                    <div className="text-lg font-black text-red-300">{r.access_attempts || 0}</div>
                  </div>
                  <div className="bg-black/40 rounded px-3 py-2 border border-red-500/15" data-testid={`ip-block-post-${r.ip}`}>
                    <div className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">Post attempts</div>
                    <div className="text-lg font-black text-red-300">{r.post_attempts || 0}</div>
                  </div>
                  <div className="bg-black/40 rounded px-3 py-2 border border-red-500/15" data-testid={`ip-block-lastseen-${r.ip}`}>
                    <div className="text-[9px] uppercase tracking-widest text-gray-500 font-bold flex items-center gap-1">
                      <Clock className="h-2.5 w-2.5" /> Last hit
                    </div>
                    <div className="text-[11px] font-bold text-gray-200 leading-tight mt-0.5">
                      {fmtDate(r.last_attempt_at || r.last_seen)}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => patch(r.ip, { nickname: r.nickname || '', reason: r.reason || '' })}
                    disabled={isBusy}
                    data-testid={`ip-block-save-${r.ip}`}
                    className="flex items-center gap-1.5 bg-red-600 hover:bg-red-500 text-white disabled:opacity-40 font-black text-[11px] uppercase tracking-widest px-3 py-1.5 rounded"
                  >
                    <Save className="h-3.5 w-3.5" />
                    Save
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default IpBlocklistTab;
